// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
// Importa um S-25b/S-45b preenchido fora do sistema (PDF oficial com campos).
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { FileUp, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRIMESTERS = ['1º Trimestre', '2º Trimestre', '3º Trimestre', '4º Trimestre'];

export default function ImportAuditPdfDialog({ open, onOpenChange, formType, formLabel, category, years, onImported }) {
  const [year, setYear] = useState('');
  const [trimester, setTrimester] = useState('');
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const isS26 = formType === 's26';

  const handleImport = async () => {
    if (!isS26 && (!year || !trimester)) { toast.error('Selecione o ano e o trimestre'); return; }
    if (!file) { toast.error('Escolha o arquivo PDF preenchido'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('form_type', formType);
      fd.append('title', isS26 ? 'auto' : `${year} - ${trimester}`);
      fd.append('category', category);
      const { data } = await axios.post(`${API}/audit-forms/import-pdf`, fd);
      const fv = data.field_values || {};
      const periodo = isS26
        ? (data.title ? ` Folha criada: ${data.title}.` : '')
        : (fv['900_2_Text_C'] && fv['900_3_Text_C']
          ? ` Período no PDF: ${fv['900_2_Text_C']} a ${fv['900_3_Text_C']}.` : '');
      toast.success(`Relatório ${formLabel} importado! ${data.fields_imported} campo(s) reconhecido(s).${periodo}`, { duration: 8000 });
      onOpenChange(false);
      setYear(''); setTrimester(''); setFile(null);
      onImported?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar PDF');
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="import-pdf-dialog">
        <DialogHeader>
          <DialogTitle>Importar {formLabel} preenchido</DialogTitle>
          <DialogDescription>
            {isS26
              ? 'Envie o PDF oficial da Folha de Contas (S-26) preenchida fora do sistema. O mês é detectado automaticamente e a folha do mês é criada.'
              : `Envie o PDF oficial do ${formLabel} preenchido fora do sistema. Os campos serão reconhecidos automaticamente e o relatório poderá ser editado normalmente.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {!isS26 && (<>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Ano</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="import-select-year"><SelectValue placeholder="Selecione o ano..." /></SelectTrigger>
              <SelectContent>
                {years.map(y => {
                  const label = y.name.replace(/_/g, ' ');
                  return <SelectItem key={y.id} value={label}>{label}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Trimestre</Label>
            <Select value={trimester} onValueChange={setTrimester}>
              <SelectTrigger data-testid="import-select-trimester"><SelectValue placeholder="Selecione o trimestre..." /></SelectTrigger>
              <SelectContent>
                {TRIMESTERS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          </>)}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">PDF preenchido</Label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="import-pdf-file-input"
              className="block w-full text-sm text-muted-foreground file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-primary file:text-primary-foreground file:text-xs file:cursor-pointer"
            />
            {file && <p className="text-xs text-muted-foreground truncate">{file.name}</p>}
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleImport} disabled={importing} data-testid="import-pdf-confirm-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
            Importar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
